"use client";

import { useState, useRef, useEffect } from "react";

interface SelectOption {
  id: string;
  label: string;
  children?: SelectOption[];
}

interface SelectDropdownProps {
  label: string;
  options: SelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  singleSelect?: boolean;
  clearOptionId?: string;
}

export default function SelectDropdown({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = "Selecione",
  singleSelect = false,
  clearOptionId,
}: SelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionId: string) => {
    if (singleSelect) {
      if (clearOptionId && optionId === clearOptionId) {
        onChange([]);
      } else if (selectedValues.includes(optionId)) {
        onChange([]);
      } else {
        onChange([optionId]);
      }
      setIsOpen(false);
      setSearchTerm("");
      return;
    }

    if (selectedValues.includes(optionId)) {
      onChange(selectedValues.filter((v) => v !== optionId));
    } else {
      onChange([...selectedValues, optionId]);
    }
  };

  const removeValue = (valueId: string) => {
    onChange(selectedValues.filter((v) => v !== valueId));
  };

  const getSelectedLabels = () => {
    const labels: { id: string; label: string }[] = [];

    const walk = (opts: SelectOption[]) => {
      opts.forEach((opt) => {
        if (selectedValues.includes(opt.id)) {
          labels.push({ id: opt.id, label: opt.label });
        }
        if (opt.children && opt.children.length > 0) {
          walk(opt.children);
        }
      });
    };

    walk(options);
    return labels;
  };

  const filterOptions = (
    opts: SelectOption[],
    term: string
  ): SelectOption[] => {
    const lowered = term.toLowerCase();
    return opts.reduce<SelectOption[]>((acc, opt) => {
      if (!singleSelect && selectedValues.includes(opt.id)) {
        return acc;
      }
      const children = opt.children ? filterOptions(opt.children, term) : [];
      const matches = term.trim()
        ? opt.label.toLowerCase().includes(lowered)
        : true;
      if (matches || children.length > 0) {
        acc.push({ ...opt, children });
      }
      return acc;
    }, []);
  };

  const filteredOptions = filterOptions(options, searchTerm);

  const selectedLabels = getSelectedLabels();
  const hasSelection = selectedLabels.length > 0;

  return (
    <div className="w-full" ref={dropdownRef}>
      <label className="block text-text-secondary text-base mb-3">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full h-12 px-5 rounded-full border border-black/23 bg-white flex items-center justify-between text-base focus:outline-none focus:border-blue-main transition-colors cursor-pointer"
        >
          <span
            className={`truncate ${hasSelection ? "text-text-primary" : "text-text-disabled"}`}
          >
            {selectedLabels.length > 0
              ? singleSelect
                ? selectedLabels[0]?.label
                : `${selectedLabels.length} selecionado(s)`
              : placeholder}
          </span>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className={`shrink-0 text-text-secondary transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-gray-200 max-h-64 overflow-hidden">
            {/* Search inside dropdown */}
            <div className="p-2 border-b border-black/10">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                className="w-full h-10 px-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-main"
              />
            </div>

            {/* Options list */}
            <div className="overflow-y-auto max-h-48">
              {filteredOptions.map((option) => (
                <div key={option.id}>
                  {renderOption(option, 0)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Selected chips */}
      {!singleSelect && selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedLabels.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-black/8 rounded-full text-[13px] text-text-primary"
            >
              {item.label}
                <button
                  type="button"
                  onClick={() => removeValue(item.id)}
                  className="hover:text-red-500 transition-colors cursor-pointer"
                >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C6.47 2 2 6.47 2 12C2 17.53 6.47 22 12 22C17.53 22 22 17.53 22 12C22 6.47 17.53 2 12 2ZM17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7L17 8.41L13.41 12L17 15.59Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  function renderOption(option: SelectOption, level: number) {
    const hasChildren = option.children && option.children.length > 0;
    const isSelected = selectedValues.includes(option.id);

    return (
      <>
        <button
          type="button"
          onClick={() => toggleOption(option.id)}
          className={`w-full py-2 text-left text-sm flex items-center gap-2 cursor-pointer transition-colors ${
            isSelected
              ? "bg-blue-main/8 text-blue-main"
              : "text-text-primary hover:bg-gray-50"
          }`}
          style={{ paddingLeft: 16 + level * 16, paddingRight: 16 }}
        >
          <span>
            {option.label}
          </span>
        </button>
        {hasChildren &&
          option.children?.map((child) => (
            <div key={child.id}>{renderOption(child, level + 1)}</div>
          ))}
      </>
    );
  }
}
